/**
 * Lightweight blog metadata — frontmatter only.
 *
 * This globs the MDX posts with `import: 'frontmatter'`, so ONLY each post's
 * YAML frontmatter object is bundled, not the compiled MDX component, its
 * images, or the MDX runtime. That keeps this module safe to import from
 * app-wide code (routeMeta, the listing pages, the prerender route list)
 * without pulling all blog content into every page's bundle.
 *
 * The renderable posts (with their MDX components) live in ./blog, which
 * should only be imported by the single-post route.
 */

export interface BlogPostMeta {
  /** Display title. */
  title: string;
  /** URL slug — the post lives at /blog/<slug>. */
  slug: string;
  /** ISO date string (publish date). */
  date: string;
  /** ISO date string (last meaningful edit); falls back to `date`. */
  modified?: string;
  /** Author display name. */
  author: string;
  /** Category label shown on cards / post header. */
  category: string;
  /** Short summary for cards, meta description, and OG tags. */
  excerpt: string;
  /** Human-readable read time, e.g. "5 min read". */
  readTime: string;
  /** Optional featured image (public/ path or absolute URL) for cards + OG. */
  featuredImage?: string;
}

// Frontmatter-only glob — the MDX default (component) export is not bundled.
const frontmatterModules = import.meta.glob<BlogPostMeta>(
  '../content/blog/*.mdx',
  { eager: true, import: 'frontmatter' }
);

const allPosts: BlogPostMeta[] = Object.values(frontmatterModules).sort(
  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
);

/** All posts (metadata only), newest first. */
export const getAllPosts = (): BlogPostMeta[] => allPosts;

/** A single post's metadata by slug, or undefined if it doesn't exist. */
export const getPostBySlug = (slug: string): BlogPostMeta | undefined =>
  allPosts.find(post => post.slug === slug);

/** Every post slug — drives the prerender route list and sitemap. */
export const getAllPostSlugs = (): string[] => allPosts.map(post => post.slug);
